<template>
  <el-alert v-if="showViewAsAlert" type="info" :closable="false" :style="alertStyle">
    <div class="view-as-alert" :class="{ 'view-as-alert-record': recordView }">
      <span v-if="loading">Loading...</span>
      <template v-else-if="recordView">
        <span>
          Viewing as <strong>{{ viewAsUserName }}</strong
          >. Rebuttals and comments you submit will still be recorded under your name.
        </span>
      </template>
      <template v-else>
        <span>View As</span>
        <el-select v-model="viewAsUserId" filterable clearable placeholder="Select user" style="width: 220px" @change="onViewAsChange">
          <el-option v-for="u in viewAsUserOptions" :key="u.key" :label="u.value" :value="u.key" :disabled="u.inactive"></el-option>
        </el-select>
        <span v-if="projectId && !viewAsUserOptions.length">No users with records on this project.</span>
      </template>
    </div>
  </el-alert>
</template>

<script>
  import { mapActions, mapState } from 'vuex'
  import { createHelpers } from 'vuex-map-fields'
  import HiaAuthorization from '@/mixins/hiaAuthorization'

  const { mapFields } = createHelpers({
    getterType: 'managedLists/projects/getField',
    mutationType: 'managedLists/projects/updateField'
  })

  export default {
    name: 'coderProjectViewAsAlert',
    mixins: [HiaAuthorization],
    props: {
      projectId: {
        type: [Number, String],
        default: null
      },
      recordView: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        projectViewAsUsers: [],
        loading: false
      }
    },
    computed: {
      ...mapFields(['viewAsUserId']),
      ...mapState('dropdowns/', ['dropdowns']),
      showViewAsAlert() {
        if (!this.systemManagement() || !this.$route.path.startsWith('/my-projects')) {
          return false
        }
        if (this.recordView) {
          return !!this.viewAsUserId
        }
        return true
      },
      viewAsUserOptions() {
        if (this.projectId && !this.recordView) {
          return this.projectViewAsUsers
        }
        return this.dropdowns.viewAsUsers.list
      },
      viewAsUserName() {
        const option = this.viewAsUserOptions.find((u) => u.key === this.viewAsUserId)
        return option ? option.value : 'Unknown user'
      },
      alertStyle() {
        return this.recordView ? { margin: '0 10px 16px 10px', minHeight: '43px' } : { marginBottom: '16px', minHeight: '43px' }
      }
    },
    watch: {
      projectId() {
        this.loadViewAsUsers()
      }
    },
    methods: {
      ...mapActions('managedLists/projects/', ['LOAD_PROJECT_VIEW_AS_USERS', 'HYDRATE_VIEW_AS_USER_ID']),
      ...mapActions('dropdowns/', ['GET_DROPDOWNS']),
      async loadViewAsUsers() {
        if (!this.showViewAsAlert && !this.systemManagement()) {
          this.projectViewAsUsers = []
          this.loading = false
          return
        }
        this.loading = true
        try {
          if (this.recordView) {
            await this.GET_DROPDOWNS(['viewAsUsers'])
          } else if (this.projectId) {
            this.projectViewAsUsers = await this.LOAD_PROJECT_VIEW_AS_USERS({ projectId: this.projectId })
          } else {
            await this.GET_DROPDOWNS(['viewAsUsers'])
          }
          if (!this.recordView) {
            this.syncViewAsUserToOptions()
          }
        } finally {
          this.loading = false
        }
      },
      syncViewAsUserToOptions() {
        if (this.viewAsUserId && !this.viewAsUserOptions.some((u) => u.key === this.viewAsUserId)) {
          this.viewAsUserId = null
        }
      },
      onViewAsChange() {
        this.syncViewAsUserToOptions()
        this.$emit('change')
      }
    },
    async created() {
      if (this.systemManagement() && this.$route.path.startsWith('/my-projects')) {
        this.HYDRATE_VIEW_AS_USER_ID()
        if (this.recordView && !this.viewAsUserId) {
          return
        }
        await this.loadViewAsUsers()
      }
    }
  }
</script>

<style scoped>
  .view-as-alert {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
  }

  .view-as-alert-record {
    width: 100%;
  }

  .view-as-alert-record >>> .el-button--text {
    padding: 0 4px;
    vertical-align: baseline;
  }

  >>> .el-alert__description {
    margin: 0px !important;
  }
</style>
